/*
 * File: page.service.ts                                                       *
 * Project: catalog-cloud                                                      *
 * File Created: Thursday, 17th February 2022 11:48:25                         *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Thursday, 2nd March 2023 15:43:07                            *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
// import { PageCell } from '../interfaces/page-cell.interface';
import { Cell, Page, PageBase, CellBase } from '@nousmedis/cc-types';
import { PageTemplate } from '@nousmedis/cc-types';
import { ApiService } from '../../api/services/api.service';
import { PageDeleteComponent } from '../components/page/page-delete/page-delete.component';
import { Fascicle } from '@nousmedis/cc-types';
import { environment } from "environment";
import { DialogService } from 'primeng/dynamicdialog';
import { FascicleHistoryService, SnapshotSummary } from './fascicle-history.service';

import { Product, CellTemplate } from '@nousmedis/cc-types';


@Injectable({
  providedIn: 'root',
})
export class PageService {
  private get urlBase(): string { return this.api.getBaseUrl(); }

  idCatalog: string = undefined;
  idFasiclce: string = undefined;
  pages: Page[] = [];
  pagesSubject = new Subject<Page[]>();
  reloadGrid$ = new Subject<boolean>();
  returnItemToToolbar$ = new Subject<string>();
  fascicle: Fascicle;
  goToIndex: number = 0;
  deletedPage: boolean = false;
  indexPreviousDelete: number = -1;

  private pageTemplates: PageTemplate[] = [];

  constructor(private api: ApiService, private dialog: DialogService, private historyService: FascicleHistoryService) { }


  takeSnapshot(operationType: SnapshotSummary['operationType']): void {
    if (this.idCatalog && this.idFasiclce && this.pages?.length > 0) {
      const pagesCopy = JSON.parse(JSON.stringify(this.pages));
      this.historyService.createSnapshot(this.idCatalog, this.idFasiclce, pagesCopy, operationType).subscribe();
    }
  }

  savePages() {
    if (this.idCatalog && this.idFasiclce) {
      let body = {
        pages: this.pages,
        totalPages: this.pages.length,
      };

      return this.api.put(
        this.urlBase + '/api/v1/catalog/' + this.idCatalog + '/fascicles/' + this.idFasiclce,
        body
      );
    } else {
      return new Observable<any>((observer) => {
        observer.next(false);
        observer.complete();
      });
    }
  }

  async reorderPages(idCatalog: string, idFascicle: string) {
    this.api
      .get(this.urlBase + '/api/v1/catalogs/' + this.idCatalog + '/update-pages')
      .subscribe((data) => {
        this.reloadPages(idCatalog, idFascicle);
      });
  }

  async reloadPages(idCatalog: string, idFascicle: any) {
    return this.api
      .get(this.urlBase + '/api/v1/catalog/' + idCatalog + '/fascicles/' + idFascicle)
      .subscribe((data) => {
        this.pages = data.pages;
        this.pagesSubject.next(this.pages);
      });
  }

  getPageTemplates() {
    return this.pageTemplates;

  }

  setPageTemplates(pageTemplates: PageTemplate[]) {
    this.pageTemplates = pageTemplates;
  }

  updatePage(page: Page) {
    return this.api.put(
      this.urlBase + '/api/v1/catalog/' +
      this.idCatalog +
      '/fascicles/' +
      this.idFasiclce +
      '/pages/' +
      page.id,
      page
    );
  }

  setPages(pages: Page[]) {
    this.pages = pages;
    this.pagesSubject.next(this.pages);


  }

  // Add Pages Last, First, before of and after of

  getNewPage(userId: string): Page {
    return {
      pageNumber: this.pages.length + 1,
      pageTemplate: this.pageTemplates[0].id,
      cells: this.addCells(this.pageTemplates[0], userId),
      id: undefined,
      createdBy: undefined
    };
  }

  addPage(userId: string) {
    this.takeSnapshot('page_save');
    let page: Page = this.getNewPage(userId);
    if (this.pages.length > 0) {
      page.pageNumber = this.pages[this.pages.length - 1].pageNumber + 1;
    } else {
      page.pageNumber = 0;
    }
    this.pages.push(page);
    this.savePages().subscribe((data) => {
      if (data) this.reorderPages(this.idCatalog, this.idFasiclce);
    });
  }

  addPageFirst(userId: string) {
    this.takeSnapshot('page_save');
    let page: Page = this.getNewPage(userId);
    page.pageNumber = this.pages[0].pageNumber;
    this.pages.unshift(page);
    this.savePages().subscribe((data) => {
      if (data) this.reorderPages(this.idCatalog, this.idFasiclce);
    });
  }

  addPageBefore(userId: string, pageNumber: number) {
    this.takeSnapshot('page_save');
    let page: Page = this.getNewPage(userId);
    let index = this.pages
      .map((p) => {
        return p.pageNumber;
      })
      .indexOf(pageNumber);

    page.pageNumber = this.pages[index].pageNumber;
    this.pages.splice(index, 0, page);
    this.savePages().subscribe((data) => {
      if (data) this.reorderPages(this.idCatalog, this.idFasiclce);
    });
  }

  addPageAfter(userId: string, pageNumber: number) {
    this.takeSnapshot('page_save');
    let page: Page = this.getNewPage(userId);
    let index = this.pages
      .map((p) => {
        return p.pageNumber;
      })
      .indexOf(pageNumber);
    if (index < this.pages.length) {
      page.pageNumber = this.pages[index].pageNumber + 1;
      this.pages.splice(index + 1, 0, page);
      this.savePages().subscribe((data) => {
        if (data) this.reorderPages(this.idCatalog, this.idFasiclce);
      });
    } else {
      this.addPage(userId);
    }
  }

  recoverItems(page: Page | Page) {
    if (page.cells) {
      page.cells.forEach((c) => {
        if (c.product) {
          this.returnItemToToolbar$.next(c.product.id);
        }
      });
    }
  }

  removeProductsOnHiddenCells(page: Page) {
    page.cells.forEach((cell: Cell) => {
      if (!cell.visible) {
        cell.product = null;
        cell.productModel = null;
      }
    });
    return page;
  }

  async removePage(id: string) {
    this.takeSnapshot('page_save');
    let removedPage = this.pages.find((p) => p.id === id);
    this.deletedPage = true;
    this.indexPreviousDelete = this.goToIndex;
    const pageNumber = removedPage.pageNumber;
    if (removedPage) {
      let index = this.pages.indexOf(removedPage);
      this.recoverItems(removedPage);
      this.pages.splice(index, 1);
      this.recalcPages();
      // alert("Index: " + index + " Pages: " + this.pages.length); ;
      //this.goToIndex = index > this.pages.length ? this.pages.length : index;

      this.savePages().subscribe((data) => {
        this.reorderPages(this.idCatalog, this.idFasiclce);
      });
    }
  }

  deleteModal(idPage: string) {
    return this.dialog
      .open(PageDeleteComponent, {
        data: {
          title: 'Eliminar página',
          type: 'yesno',
        }
      });
  }

  recalcPages() {
    const firstPage = this.pages[0] ? this.pages[0].pageNumber : 1;
    for (let i = 0; i < this.pages.length; i++) {
      this.pages[i].pageNumber = firstPage + i;
    }
  }

  addCells(pageTemplate: PageTemplate, userId: string): Cell[] {
    let totalCells = pageTemplate.columns * pageTemplate.rows;
    let cells: Cell[] = [];
    let row = 0;
    let column = 0;

    for (let cell of pageTemplate.cells) {
      cells.push({
        cellNumber: cell.cellNumber,
        columnPosition: cell.columnPosition,
        columnWith: cell.columnWith,
        rowPosition: cell.rowPosition,
        rowWidth: cell.rowWidth,
        visible: cell.visible,
        productTable: 'products_' + this.idCatalog,
        createdBy: userId,
        id: undefined,
        productModel: undefined,
        product: undefined,
        productVariant: undefined,
        productExtra: [],
      });

      column++;
    }
    return cells;
  }

  setCells(pageIndex: number, pageTemplate: PageTemplate, userId: string) {
    let cells = this.addCells(pageTemplate, userId);
    this.pages[pageIndex].cells = cells;

    this.savePages().subscribe((data) => {
      console.debug('Page Service.', 'Saved Pages');
    });
  }

  setGrid(pageIndex: number, pageTemplate: PageTemplate, userId: string) {
    this.pages[pageIndex].cells = this.addCells(pageTemplate, userId);
    this.pages[pageIndex].pageTemplate = pageTemplate.id;
    this.reloadGrid$.next(true);

    //this.pagesSubject.next(this.pages);
  }

  getPageTemplate(pageIndex: number): PageTemplate {
    let baseIndex = 0;
    if (typeof this.pages[pageIndex].pageTemplate === 'string') {
      baseIndex = this.pageTemplates
        .map((b) => b.id)
        .indexOf(this.pages[pageIndex].pageTemplate.toString());

      return this.pageTemplates[baseIndex];
    } else {

      return this.pageTemplates[0];
    }
  }

  rollBackPage(
    pageIndex: number,
    pageTemplate: string,
    cells: Cell[] | CellBase<string>[]
  ) {
    this.pages[pageIndex].cells = cells;
    this.pages[pageIndex].pageTemplate = pageTemplate;
    this.pagesSubject.next(this.pages);
  }
}
